<script lang="ts">
  import { pmStore } from '../stores/pm.svelte.ts';
  import type { ViewConfig } from '../types.ts';
  import TaskForm from './TaskForm.svelte';
  import QueuePanel from './QueuePanel.svelte';
  import RunningPanel from './RunningPanel.svelte';
  import BoardView from './BoardView.svelte';
  import ProjectOverview from './ProjectOverview.svelte';
  import PulseView from './PulseView.svelte';
  import CrossRepoBoard from './CrossRepoBoard.svelte';
  import CrossRepoTable from './CrossRepoTable.svelte';
  import CanvasView from './CanvasView.svelte';
  import ActivityFeed from './ActivityFeed.svelte';
  import MyTasksView from './MyTasksView.svelte';
  import GoalsView from './GoalsView.svelte';
  import PortfolioView from './PortfolioView.svelte';
  import WorkloadView from './WorkloadView.svelte';
  import RulesView from './RulesView.svelte';
  import PersonasView from './PersonasView.svelte';
  import TableView from './TableView.svelte';
  import GalleryView from './GalleryView.svelte';
  import MilestonesView from './MilestonesView.svelte';
  import BrandAssetsView from './BrandAssetsView.svelte';
  import DrivePane from './DrivePane.svelte';
  import StudioCanvas from './StudioCanvas.svelte';
  import DevView from './DevView.svelte';
  import ChatPane from './ChatPane.svelte';

  type Tab =
    | 'overview'
    | 'board'
    | 'table'
    | 'gallery'
    | 'canvas'
    | 'agents'
    | 'activity'
    | 'tasks'
    | 'goals'
    | 'milestones'
    | 'portfolio'
    | 'workload'
    | 'pulse'
    | 'rules'
    | 'personas'
    | 'chat'
    | 'brand'
    | 'drive'
    | 'studio'
    | 'dev';

  interface Props {
    activeTab: Tab;
    activeViewConfig?: ViewConfig | null;
    devFileToOpen?: string | null;
    onFileOpened?: () => void;
    onOpenCard: (id: string) => void;
    onOpenProjectBoard: (projectId: string) => void;
    onSelectAgent: (id: string) => void;
  }

  let {
    activeTab,
    activeViewConfig = null,
    devFileToOpen = null,
    onFileOpened,
    onOpenCard,
    onOpenProjectBoard,
    onSelectAgent,
  }: Props = $props();
</script>

{#if activeViewConfig}
  {#if activeViewConfig.view_type === 'table'}
    <CrossRepoTable viewConfig={activeViewConfig} onOpenCard={onOpenCard} />
  {:else}
    <CrossRepoBoard viewConfig={activeViewConfig} onOpenCard={onOpenCard} />
  {/if}
{:else if activeTab === 'overview'}
  <ProjectOverview />
{:else if activeTab === 'board'}
  {#if pmStore.activeBoardId}
    <BoardView
      boardId={pmStore.activeBoardId}
      onOpenCard={onOpenCard}
    />
  {:else}
    <div class="empty-state">
      <p>No board selected. Create a workspace and project to get started.</p>
    </div>
  {/if}
{:else if activeTab === 'table'}
  {#if pmStore.activeBoardId}
    <TableView
      boardId={pmStore.activeBoardId}
      onOpenCard={onOpenCard}
    />
  {:else}
    <div class="empty-state">No board selected.</div>
  {/if}
{:else if activeTab === 'gallery'}
  {#if pmStore.activeBoardId}
    <GalleryView boardId={pmStore.activeBoardId} onOpenCard={onOpenCard} />
  {:else}
    <div class="empty-state">No board selected.</div>
  {/if}
{:else if activeTab === 'canvas'}
  {#if pmStore.activeBoardId}
    <CanvasView
      boardId={pmStore.activeBoardId}
      onOpenCard={onOpenCard}
    />
  {:else}
    <div class="empty-state">Select a board to use the Canvas view.</div>
  {/if}
{:else if activeTab === 'activity'}
  <div class="activity-wrapper">
    <ActivityFeed />
  </div>
{:else if activeTab === 'tasks'}
  <MyTasksView onOpenCard={onOpenCard} />
{:else if activeTab === 'goals'}
  <GoalsView />
{:else if activeTab === 'milestones'}
  {#if pmStore.activeProjectId}
    <MilestonesView />
  {:else}
    <div class="empty-state">Select a project to view milestones.</div>
  {/if}
{:else if activeTab === 'portfolio'}
  <PortfolioView onOpenCard={onOpenCard} onOpenBoard={onOpenProjectBoard} />
{:else if activeTab === 'workload'}
  <WorkloadView />
{:else if activeTab === 'pulse'}
  <PulseView />
{:else if activeTab === 'rules'}
  {#if pmStore.activeWorkspaceId}
    <RulesView workspaceId={pmStore.activeWorkspaceId} />
  {:else}
    <div class="empty-state">Select a workspace to manage rules.</div>
  {/if}
{:else if activeTab === 'personas'}
  <PersonasView />
{:else if activeTab === 'chat'}
  <ChatPane />
{:else if activeTab === 'brand'}
  <BrandAssetsView />
{:else if activeTab === 'drive'}
  <DrivePane />
{:else if activeTab === 'studio'}
  <StudioCanvas />
{:else if activeTab === 'dev'}
  <DevView fileToOpen={devFileToOpen} onFileOpened={onFileOpened} />
{:else}
  <div class="agents-layout">
    <aside class="agents-sidebar">
      <section class="section-card">
        <h3 class="section-title">New Task</h3>
        <TaskForm />
      </section>
    </aside>
    <div class="agents-content">
      <RunningPanel onSelectAgent={onSelectAgent} />
      <QueuePanel />
    </div>
  </div>
{/if}

<style>
  .empty-state {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: oklch(0.72 0.08 310 / 0.55);
    font-size: 0.9rem;
    font-weight: 300;
    letter-spacing: 0.02em;
    position: relative;
    overflow: hidden;
  }

  .empty-state::before,
  .empty-state::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    border-radius: 50%;
    pointer-events: none;
  }

  .empty-state::before {
    width: 320px;
    height: 320px;
    margin: -160px 0 0 -160px;
    border: 1px solid oklch(0.72 0.28 310 / 0.18);
    box-shadow:
      inset 0 0 80px oklch(0.62 0.28 310 / 0.06),
      0 0 60px oklch(0.62 0.28 310 / 0.05);
    will-change: transform, opacity;
    animation: empty-ring-pulse calc(var(--pulse-rate, 10s) * 1.4) ease-in-out infinite;
  }

  .empty-state::after {
    width: 560px;
    height: 560px;
    margin: -280px 0 0 -280px;
    border: 1px solid oklch(0.55 0.20 265 / 0.14);
    box-shadow:
      inset 0 0 100px oklch(0.42 0.20 265 / 0.04),
      0 0 40px oklch(0.78 0.16 85 / 0.03);
    will-change: transform, opacity;
    animation: empty-ring-pulse calc(var(--pulse-rate, 10s) * 1.8) ease-in-out infinite;
    animation-delay: calc(var(--pulse-rate, 10s) * -0.4);
  }

  .empty-state > * {
    position: relative;
    z-index: 1;
  }

  @keyframes empty-ring-pulse {
    0%, 100% { transform: scale(0.92); opacity: 0.45; }
    50% { transform: scale(1.02); opacity: 1; }
  }

  .agents-layout {
    display: grid;
    grid-template-columns: 340px 1fr;
    gap: 1.5rem;
    padding: 1.5rem 1.75rem;
    max-width: 1400px;
    width: 100%;
    margin: 0 auto;
    box-sizing: border-box;
    align-items: start;
    overflow-y: auto;
    flex: 1;
  }

  .agents-sidebar {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .section-card {
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 10px;
    padding: 1.125rem 1.25rem;
  }

  .section-title {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: rgba(255, 255, 255, 0.3);
    margin: 0 0 0.875rem;
  }

  .agents-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .activity-wrapper {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  @media (max-width: 900px) {
    .agents-layout {
      grid-template-columns: 1fr;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .empty-state::before,
    .empty-state::after {
      animation: none;
    }
  }
</style>
